import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import image from '../../images/pngs/placeholder.png';
import { useCart } from '../../Contexts/CartContext';
import Loader from '../../Components/utils/Loader';
import { LoginContext } from '../../Contexts/LoginContext';
import { formatCurrencyDual } from '../../data/constants';

export default function CartDrawer({ isOpen, toggleDrawer }) {
  const drawerRef = useRef(null);
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity, isLoading, taxRate } = useCart();

  const [deliveryType, setDeliveryType] = useState('delivery');

  const filteredCartItems = cartItems.filter((item) => item.deliveryType === deliveryType);

  const deliveryLineCount = cartItems.filter((item) => item.deliveryType === 'delivery').length;
  const pickupLineCount = cartItems.filter((item) => item.deliveryType === 'pickup').length;

  const { feesObj, currency, exchangeRate } = useContext(LoginContext);

  /**
   * The buyer fee varies by listing type and destination, so the cart endpoint
   * resolves it per line. `feesObj.buyer` only covers lines whose listing could
   * not be loaded.
   */
  const buyerFeeOf = (item) =>
    Number(item.buyerFeePercent ?? feesObj.buyer) || 0;

  const handleCheckout = () => {
    const orderItemsWithSummary = filteredCartItems.map((item) => {
      const addedExtra = Number(item.addedServicesTotal) || 0;
      const itemSubtotal = item.price * item.quantity + addedExtra;
      const itemDeliveryFee = item.deliveryType === 'delivery' ? item.deliveryFee || 0 : 0;
      const itemServiceFee = (itemSubtotal + itemDeliveryFee) * (buyerFeeOf(item) / 100);
      const itemTax = (itemSubtotal + itemDeliveryFee + itemServiceFee) * (taxRate / 100);
      const itemTotal = itemSubtotal + itemDeliveryFee + itemServiceFee + itemTax;

      let selectedOptions = item.selectedOptions;
      if (item.pricingType === 'variable') {
        const variables = selectedOptions.variablePricing.variables.map((varItem) => {
          if (varItem.selectedOption.name === undefined) return null;
          return varItem;
        });
        selectedOptions = {
          variablePricing: {
            variables: variables.filter((v) => v !== null),
            basePrice: selectedOptions.variablePricing.basePrice,
          },
        };
      }

      return {
        cartItemId: item._id,
        productId: item.productId,
        quantity: item.quantity,
        selectedOptions: selectedOptions,
        price: item.price,
        deliveryType: item.deliveryType,
        name: item.name,
        thumbnail: item.thumbnail,
        pricingType: item.pricingType,
        isB2B: Boolean(item.isB2B),
        addedServices: item.addedServices || [],
        addedServicesTotal: addedExtra,
        summary: {
          price: item.price,
          quantity: item.quantity,
          subtotal: itemSubtotal,
          deliveryFee: itemDeliveryFee,
          serviceFee: itemServiceFee,
          tax: itemTax,
          total: itemTotal,
        },
        orderType: 'single',
        amountToPay: itemTotal,
      };
    });

    const aggregateSummary = {
      subtotal: orderItemsWithSummary.reduce((acc, item) => acc + item.summary.subtotal, 0),
      deliveryFee: orderItemsWithSummary.reduce((acc, item) => acc + item.summary.deliveryFee, 0),
      serviceFee: orderItemsWithSummary.reduce((acc, item) => acc + item.summary.serviceFee, 0),
      tax: orderItemsWithSummary.reduce((acc, item) => acc + item.summary.tax, 0),
      total: orderItemsWithSummary.reduce((acc, item) => acc + item.summary.total, 0),
    };

    navigate('/checkoutPage', {
      state: {
        orderItems: orderItemsWithSummary,
        aggregateSummary,
        fromCart: true,
      },
    });
    toggleDrawer();
  };

  useEffect(() => {

    const handleClickOutside = (event) => {
      if (drawerRef.current && !drawerRef.current.contains(event.target) && isOpen) {
        toggleDrawer();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, toggleDrawer]);

  const increaseQuantity = (item) => {
    updateQuantity(item.productId, item.selectedOptions, item.deliveryType, item.quantity + 1, item._id);
  };

  const decreaseQuantity = (item) => {
    if (item.quantity > 1) {
      updateQuantity(item.productId, item.selectedOptions, item.deliveryType, item.quantity - 1, item._id);
    }
  };

  /** Build a short human-readable label from selectedOptions (e.g. "Color: Red · Size: L") */
  const getVariantLabel = (selectedOptions) => {
    if (!selectedOptions) return null;
    const parts = [];
    // Fixed pricing: packageName
    if (selectedOptions.fixedPricing?.packageName) {
      parts.push(selectedOptions.fixedPricing.packageName);
    }
    // Variable pricing: each variable name + selected option
    if (selectedOptions.variablePricing?.variables?.length) {
      selectedOptions.variablePricing.variables.forEach(v => {
        if (v?.name && v?.selectedOption?.name) {
          parts.push(`${v.name}: ${v.selectedOption.name}`);
        }
      });
    }
    return parts.length > 0 ? parts.join(' · ') : null;
  };

  const subtotal = filteredCartItems.reduce(
    (acc, item) => acc + item.price * item.quantity + (Number(item.addedServicesTotal) || 0),
    0
  );
  const totalDeliveryFee = filteredCartItems.reduce(
    (acc, item) => acc + (item.deliveryFee || 0),
    0
  );
  const serviceFee = filteredCartItems.reduce((acc, item) => {
    const lineBase =
      item.price * item.quantity +
      (Number(item.addedServicesTotal) || 0) +
      (item.deliveryFee || 0);
    return acc + lineBase * (buyerFeeOf(item) / 100);
  }, 0);
  const tax = (subtotal + totalDeliveryFee + serviceFee) * (taxRate / 100);
  const total = subtotal + serviceFee + totalDeliveryFee + tax;

  const handleItemClick = (item, e) => {
    // If the click is on the remove button or quantity controls, ignore
    if (e.target.closest('.cartItemRemove') || e.target.closest('.quantityControl')) return;

    toggleDrawer();
    const url = item.isB2B 
      ? `/productDetail/${item.productId}?market=b2b` 
      : `/productDetail/${item.productId}`;

    navigate(url, {
      state: {
        fromCart: true,
        quantity: item.quantity,
        selectedOptions: item.selectedOptions,
        addedServices: item.addedServices,
        deliveryType: item.deliveryType
      }
    });
  };

  return (
    <div className={`cartDrawer ${isOpen ? 'open' : ''}`} ref={drawerRef}>
      <div className="cartDrawerHeader">
        <div className="deliveryToggle">
          <button
            className={deliveryType === 'delivery' ? 'active' : ''}
            onClick={() => setDeliveryType('delivery')}
            type="button"
          >
            Delivery
            <span className="cartTabCount">{deliveryLineCount}</span>
          </button>
          <button
            className={deliveryType === 'pickup' ? 'active' : ''}
            onClick={() => setDeliveryType('pickup')}
            type="button"
          >
            Pickup
            <span className="cartTabCount">{pickupLineCount}</span>
          </button>
        </div>
        <button className="closeButton" onClick={toggleDrawer}>×</button>
      </div>

      <div className="cartItems">
        {isLoading ? (
          <Loader type="simpleMini" />
        ) : filteredCartItems.length > 0 ? (
          filteredCartItems.map((item) => (
            <div
              key={item._id}
              className="cartItem"
              onClick={(e) => handleItemClick(item, e)}
              style={{ cursor: 'pointer' }}
            >
              <div
                className="cartItemRemove"
                onClick={() => removeFromCart(item.productId, item._id)}
              >
                ×
              </div>
              <div className="cartItemImage">
                <img src={item.thumbnail || image} alt={item.name} />
              </div>
              <div className="cartItemDetails">
                <h3>
                  {item.isB2B && <span className="cartItemB2BTag">B2B</span>}
                  {item.name}
                </h3>
                {/* Variation label — shows selected options so buyers can distinguish same-product rows */}
                {(() => {
                  const label = getVariantLabel(item.selectedOptions);
                  return label ? (
                    <div className="cartItemVariantLabel" style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px', marginBottom: '2px' }}>
                      {label}
                    </div>
                  ) : null;
                })()}
                {item.addedServices && item.addedServices.length > 0 && (
                  <div className="cartItemAddedServicesList" style={{ marginTop: '4px', fontSize: '11px', color: '#6b7280' }}>
                    {item.addedServices.map((svc, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                        <span>+ {svc.name} {svc.pricingMode === 'perUnit' ? `(×${svc.qty || 1})` : '(per order)'}</span>
                        <span>{formatCurrencyDual((parseFloat(svc.price) || 0) * (svc.qty || 1), currency, exchangeRate)}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="cartItemPriceQuantity">
                  <span className="itemPrice">
                    {formatCurrencyDual(
                      item.price * item.quantity + (Number(item.addedServicesTotal) || 0),
                      currency,
                      exchangeRate
                    )}
                  </span>
                  <div className="quantityControl">
                    <button
                      onClick={() => decreaseQuantity(item)}
                    >
                      -
                    </button>
                    <span>{item.quantity}</span>
                    <button
                      onClick={() => increaseQuantity(item)}
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <p className="emptyCart">Your cart is empty for {deliveryType}</p>
        )}
      </div>

      <div className="checkoutSection">
        <div className="checkoutRow">
          <span>Subtotal</span>
          <span>{formatCurrencyDual(subtotal, currency, exchangeRate)}</span>
        </div>
        <div className="checkoutRow">
          <span>Delivery Fee</span>
          <span>{formatCurrencyDual(totalDeliveryFee, currency, exchangeRate)}</span>
        </div>
        <div className="checkoutRow">
          <span>Service Fee</span>
          <span>{formatCurrencyDual(serviceFee, currency, exchangeRate)}</span>
        </div>
        <div className="checkoutRow">
          <span>Tax</span>
          <span>{formatCurrencyDual(tax, currency, exchangeRate)}</span>
        </div>
        <div className="checkoutTotal">
          <span>Total</span>
          <span>{formatCurrencyDual(total, currency, exchangeRate)}</span>
        </div>
        <button
          className="checkoutButton"
          onClick={handleCheckout}
          disabled={filteredCartItems.length === 0 || isLoading}
        >
          Checkout
        </button>
      </div>
    </div>
  );
}